/*
 * /cart page behaviour - CART-PAGE-2026-09-11.md Step 4. Progressively
 * enhances the server-rendered, no-JS-first markup in sections/main-cart.
 * liquid: every quantity/remove/quit-together/upsell control is a real
 * <a href="/cart/change?..."> or <a href="/cart/add?...">, and Checkout is
 * a real <button type="submit" name="checkout"> inside a plain <form
 * action="/cart">. Nothing here is required for the page to work; it only
 * replaces full-page navigations with in-place updates.
 *
 * Deliberately does NOT dispatch `cart:updated` - assets/cart-drawer.js
 * listens for that event and opens the drawer, which must never happen on
 * the cart page itself (rule 9: the nav cart icon stays a plain link here,
 * the full page already IS the cart).
 */
(() => {
  const root = document.querySelector('[data-section-id].main-cart');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  const UNDO_TIMEOUT_MS = 6000;

  // ---- badge (no cart:updated - see file doc comment) --------------------

  function updateBadge(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.hidden = cart.item_count === 0;
      badge.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
      badge.classList.toggle('nav-cart__count--wide', cart.item_count > 9);
    });
  }

  // ---- section refresh -----------------------------------------------

  async function fetchCartAndSection() {
    const [cartRes, sectionRes] = await Promise.all([
      fetch('/cart.js', { headers: { Accept: 'application/json' } }),
      fetch(`/?sections=${sectionId}`, { headers: { Accept: 'application/json' } }),
    ]);
    const cart = await cartRes.json();
    const sections = await sectionRes.json();
    return { cart, html: sections[sectionId] };
  }

  function swapRoot(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('.main-cart');
    if (!fresh) return;
    root.innerHTML = fresh.innerHTML;
    observeSummary();
    insertUndoRow();
  }

  async function refresh() {
    const { cart, html } = await fetchCartAndSection();
    swapRoot(html);
    updateBadge(cart);
    return cart;
  }

  // ---- quantity / remove ------------------------------------------------

  async function sendQtyChange(key, quantity) {
    let res;
    try {
      res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
    } catch (err) {
      console.error(err);
      return;
    }

    if (quantity === 0) {
      if (res.ok) {
        const lineEl = root.querySelector(`[data-cart-line][data-line-key="${key}"]`);
        if (lineEl) startUndo(lineEl, key);
      }
      await refresh();
      return;
    }

    // An over-requested quantity is clamped to what's available server-
    // side either way, but Shopify's actual response shape for that
    // varies: sometimes 200 with the clamped cart already in the body,
    // sometimes 422 with only a message (the clamp still happened, just
    // not reported inline - confirmed against a real low-stock variant).
    // Either way, re-fetch /cart.js for the one true post-change quantity
    // rather than assuming which shape came back.
    let changed;
    if (res.ok) {
      const cart = await res.json();
      changed = cart.items.find((item) => item.key === key);
    } else {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      changed = cart.items.find((item) => item.key === key);
    }

    // Refresh FIRST (it's a fresh, correct server render with the
    // clamped quantity already in it), then find that same line again in
    // the new DOM and populate its error slot - setting it before
    // refreshing would just get overwritten the instant the section
    // swap runs.
    const needsStockError = Boolean(changed) && changed.quantity < quantity;
    await refresh();

    if (needsStockError) {
      const freshLine = root.querySelector(`[data-cart-line][data-line-key="${key}"]`);
      const freshError = freshLine?.querySelector('[data-cart-line-error]');
      if (freshError) window.showCartStockError(freshError, changed);
    }
  }

  // Bug #7 (FIX-PLAN-2026-09-11): a fast double-tap on +/- both read the
  // same not-yet-refreshed DOM quantity as "current", so the second tap's
  // request undid the first instead of building on it (a step was lost).
  // desiredQty tracks the latest target per line key regardless of what's
  // in flight; a pump loop per key sends one request at a time and keeps
  // going as long as a newer target has been queued since the last send,
  // so a tap during a request is queued, not dropped, and taps always
  // compose off the latest intended quantity rather than the stale DOM.
  const desiredQty = new Map();
  const pumping = new Set();

  function displayedQty(key) {
    const line = root.querySelector(`[data-cart-line][data-line-key="${key}"]`);
    const valueEl = line?.querySelector('[data-cart-qty-value]');
    return parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
  }

  async function pumpQty(key) {
    if (pumping.has(key)) return;
    pumping.add(key);
    try {
      let lastSent;
      while (desiredQty.has(key) && desiredQty.get(key) !== lastSent) {
        lastSent = desiredQty.get(key);
        await sendQtyChange(key, lastSent);
      }
      desiredQty.delete(key);
    } finally {
      pumping.delete(key);
    }
  }

  function queueQtyDelta(key, delta) {
    const baseline = desiredQty.has(key) ? desiredQty.get(key) : displayedQty(key);
    desiredQty.set(key, Math.max(0, baseline + delta));
    pumpQty(key);
  }

  function queueQtySet(key, quantity) {
    desiredQty.set(key, quantity);
    pumpQty(key);
  }

  // ---- undo ------------------------------------------------------------

  function startUndo(lineEl, key) {
    const variantId = lineEl.getAttribute('data-variant-id');
    const titleEl = lineEl.querySelector('.cart-line__title');
    const variantEl = lineEl.querySelector('.cart-line__variant');
    const quantityEl = lineEl.querySelector('[data-cart-qty-value]');
    const title = titleEl ? titleEl.textContent.trim() : '';
    const variant = variantEl ? variantEl.textContent.trim() : '';
    const quantity = parseInt(quantityEl ? quantityEl.textContent : '1', 10) || 1;

    pendingUndo = { variantId, quantity, title, variant };
  }

  let pendingUndo = null;

  function insertUndoRow() {
    if (!pendingUndo) return;
    // Permanent slot outside the filled/empty if-else (sections/main-cart.
    // liquid) so it survives removing the last item, which swaps
    // [data-cart-items] itself out of the DOM.
    const slotEl = root.querySelector('[data-cart-undo-slot]');
    if (!slotEl) {
      pendingUndo = null;
      return;
    }

    const label = pendingUndo.variant
      ? window.__cartPageStrings.removedWithVariant.replace('{{ title }}', pendingUndo.title).replace('{{ variant }}', pendingUndo.variant)
      : window.__cartPageStrings.removed.replace('{{ title }}', pendingUndo.title);

    const row = document.createElement('div');
    row.className = 'cart-undo-row';
    row.innerHTML = `
      <p class="cart-undo-row__text">${label}</p>
      <button type="button" class="cart-undo-row__button" data-undo-button>${window.__cartPageStrings.undo}</button>
    `;
    slotEl.prepend(row);

    const captured = pendingUndo;
    pendingUndo = null;

    const timer = setTimeout(() => {
      row.remove();
    }, UNDO_TIMEOUT_MS);

    row.querySelector('[data-undo-button]').addEventListener('click', async () => {
      clearTimeout(timer);
      row.remove();
      if (!captured.variantId) return;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(captured.variantId), quantity: captured.quantity }] }),
        });
      } catch (err) {
        console.error(err);
      }
      await refresh();
    });
  }

  // ---- upsell add --------------------------------------------------------

  async function addUpsell(link) {
    const row = link.closest('[data-cart-upsell-row]');
    const variantId = row?.getAttribute('data-variant-id');
    if (!variantId) return;

    link.classList.add('is-disabled');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('Add failed');
      await refresh();
    } catch (err) {
      console.error(err);
      link.classList.remove('is-disabled');
    }
  }

  // ---- click delegation ---------------------------------------------------
  // `root` is never replaced (only its innerHTML is, in swapRoot), so this
  // single listener keeps working across every server-rendered refresh.

  root.addEventListener('click', (e) => {
    const line = e.target.closest('[data-cart-line]');
    if (line) {
      const control = e.target.closest(
        '[data-cart-qty-increase], [data-cart-qty-decrease], [data-cart-remove], [data-quit-together-add]',
      );
      if (!control) return;
      e.preventDefault();
      const key = line.getAttribute('data-line-key');

      if (control.hasAttribute('data-cart-qty-increase')) {
        queueQtyDelta(key, 1);
      } else if (control.hasAttribute('data-cart-qty-decrease')) {
        queueQtyDelta(key, -1);
      } else if (control.hasAttribute('data-cart-remove')) {
        queueQtySet(key, 0);
      } else if (control.hasAttribute('data-quit-together-add')) {
        queueQtySet(key, 2);
      }
      return;
    }

    const upsellBtn = e.target.closest('[data-cart-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      addUpsell(upsellBtn);
    }
    // The sticky checkout bar's button needs no handler here - it's a
    // real <button type="submit" form="MainCartForm-..."> that submits
    // the cart form natively (sections/main-cart.liquid).
  });

  // ---- mobile sticky bar --------------------------------------------------

  let summaryObserver = null;

  function observeSummary() {
    const bar = root.querySelector('[data-sticky-bar]');
    const card = root.querySelector('[data-cart-summary-card]');
    if (!bar || !card) return;

    if (summaryObserver) summaryObserver.disconnect();

    if (window.matchMedia('(min-width: 990px)').matches) {
      bar.classList.remove('is-visible');
      return;
    }

    summaryObserver = new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 },
    );
    summaryObserver.observe(card);
  }

  window.addEventListener('resize', observeSummary);
  observeSummary();
})();
